<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { fetchContactDetailsWithCache } from 'dashboard/helper/contactDetailsCache';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactInfo from 'dashboard/routes/dashboard/conversation/contact/ContactInfo.vue';
import ContactNotes from 'dashboard/routes/dashboard/conversation/contact/ContactNotes.vue';
import CustomAttributes from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributes.vue';
import ContactMetrics from 'dashboard/components-next/ContactMetrics/ContactMetrics.vue';
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  contactId: { type: [Number, String], default: null },
  excludeConversationId: { type: [Number, String], default: null },
});

const emit = defineEmits(['close', 'open-conversation']);

const store = useStore();
const { isContactSidebarItemOpen, toggleSidebarUIState } = useUISettings();

const isLoading = ref(false);

const normalizedContactId = computed(() => {
  const id = Number(props.contactId);
  return id > 0 ? id : null;
});

const normalizedExcludeId = computed(() => {
  const id = Number(props.excludeConversationId);
  return id > 0 ? id : 0;
});

const contactGetter = computed(() => store.getters['contacts/getContact']);
const contact = computed(() => {
  if (!normalizedContactId.value) return null;
  const getter = contactGetter.value;
  return typeof getter === 'function' ? getter(normalizedContactId.value) : null;
});

// Previous conversations (raw snake_case from store, same format ConversationCard expects)
const contactConversations = computed(() => {
  if (!normalizedContactId.value) return [];
  const getter = store.getters['contactConversations/getContactConversation'];
  if (typeof getter !== 'function') return [];
  const all = getter(normalizedContactId.value) || [];
  return all.filter(conv => Number(conv.id) !== normalizedExcludeId.value);
});

watch(
  () => props.contactId,
  async id => {
    if (!id || Number(id) <= 0) return;
    isLoading.value = true;
    await Promise.allSettled([
      fetchContactDetailsWithCache(store, Number(id)),
      store.dispatch('contactConversations/get', Number(id)),
    ]);
    isLoading.value = false;
  },
  { immediate: true }
);

watch(
  () => props.show,
  val => {
    if (val) store.dispatch('attributes/get', 0).catch(() => null);
  }
);
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-[11000] bg-black/30"
        @click.self="emit('close')"
      />
    </transition>

    <transition
      enter-active-class="transition-transform duration-200 ease-out"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-150 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div
        v-if="show"
        class="fixed right-0 top-0 h-full w-[380px] max-w-[95vw] z-[11001] flex flex-col bg-n-solid-1 border-l border-n-weak shadow-xl"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak flex-shrink-0">
          <span class="text-sm font-semibold text-n-slate-12">{{ $t('CONVERSATION.SIDEBAR.CONTACT') }}</span>
          <button
            class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-11"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto">
          <!-- Loading skeleton -->
          <div
            v-if="isLoading && !contact"
            class="flex items-center gap-3 px-4 py-3 border-b border-n-weak"
          >
            <div class="size-10 rounded-full bg-n-alpha-2 animate-pulse flex-shrink-0" />
            <div class="flex flex-col gap-1.5 flex-1">
              <div class="h-3 w-28 rounded bg-n-alpha-2 animate-pulse" />
              <div class="h-2.5 w-36 rounded bg-n-alpha-2 animate-pulse" />
            </div>
          </div>

          <template v-else-if="contact">
            <!-- Contact Info — same component as conversation sidebar -->
            <ContactInfo :contact="contact" @panel-close="emit('close')" />

            <!-- Contact Metrics -->
            <div class="px-2 pt-3">
              <AccordionItem
                :title="$t('CONTACT_METRICS.PANEL_TITLE')"
                :is-open="isContactSidebarItemOpen('is_contact_metrics_open')"
                compact
                @toggle="val => toggleSidebarUIState('is_contact_metrics_open', val)"
              >
                <ContactMetrics :contact-id="normalizedContactId" />
              </AccordionItem>
            </div>

            <div class="px-2 pb-8 mt-3 flex flex-col gap-3">
              <!-- Contact Attributes -->
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
                :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
                compact
                @toggle="val => toggleSidebarUIState('is_contact_attributes_open', val)"
              >
                <CustomAttributes
                  attribute-type="contact_attribute"
                  attribute-from="conversation_contact_panel"
                  :contact-id="normalizedContactId"
                  :empty-state-message="$t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')"
                />
              </AccordionItem>

              <!-- Previous Conversations -->
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')"
                :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
                compact
                @toggle="val => toggleSidebarUIState('is_previous_conv_open', val)"
              >
                <div v-if="contactConversations.length">
                  <!-- Wrapper intercepts click to open ConversationDock instead of navigating -->
                  <div
                    v-for="conv in contactConversations"
                    :key="`drawer-conv-${conv.id}`"
                    class="cursor-pointer"
                    @click.capture.stop="emit('open-conversation', conv.id)"
                  >
                    <ConversationCard
                      :chat="conv"
                      hide-thumbnail
                      :enable-context-menu="false"
                    />
                  </div>
                </div>
                <p
                  v-else
                  class="no-label-message px-4 py-3 text-n-slate-11 text-sm"
                >
                  {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
                </p>
              </AccordionItem>

              <!-- Contact Notes -->
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
                :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
                compact
                @toggle="val => toggleSidebarUIState('is_contact_notes_open', val)"
              >
                <ContactNotes :contact-id="normalizedContactId" />
              </AccordionItem>
            </div>
          </template>

          <div
            v-else
            class="flex items-center justify-center py-16 text-sm text-n-slate-11"
          >
            Contato não encontrado
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style>
/* floating-vue (v-tooltip) z-index above contact drawer (z-11001) */
.v-popper__popper { z-index: 11500 !important; }
</style>
